/**
 * Zone 3a — 7-step funnel visualization.
 * Compact vertical funnel with proportional bars and conversion rates.
 */

import { t } from "../i18n";

type FunnelStep = {
  readonly step_name: string;
  readonly user_count: number;
};

type FunnelMiniProps = {
  readonly steps: ReadonlyArray<FunnelStep>;
  readonly onStepClick?: (stepName: string) => void;
};

const STEP_KEY_MAP: Record<string, string> = {
  register: "funnel.step_register",
  first_conversation: "funnel.step_conversation",
  first_summary: "funnel.step_summary",
  first_delivery: "funnel.step_delivery",
  first_share: "funnel.step_share",
  first_storage: "funnel.step_storage",
  first_payment: "funnel.step_payment",
};

function resolveStepLabel(stepName: string): string {
  const key = STEP_KEY_MAP[stepName];
  return key !== undefined ? t(key) : stepName;
}

function resolveBarColor(
  count: number,
  firstCount: number,
  prevCount: number,
): string {
  if (firstCount === 0) return "#111111";
  const ratioToFirst = count / firstCount;
  if (ratioToFirst < 0.05) return "#f59e0b";
  const ratioToPrev = prevCount > 0 ? count / prevCount : 1;
  if (ratioToPrev < 0.5) return "#dc2626";
  return "#111111";
}

type StepRowProps = {
  readonly step: FunnelStep;
  readonly widthPercent: number;
  readonly barColor: string;
  readonly onStepClick?: (stepName: string) => void;
};

function StepRow({
  step,
  widthPercent,
  barColor,
  onStepClick,
}: StepRowProps): React.JSX.Element {
  const isClickable = typeof onStepClick === "function";

  function handleClick(): void {
    onStepClick?.(step.step_name);
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (e.key === "Enter" || e.key === " ") {
      onStepClick?.(step.step_name);
    }
  }

  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      style={{
        ...s.stepRow,
        cursor: isClickable ? "pointer" : "default",
      }}
    >
      <div style={s.stepHeader}>
        <span style={s.stepLabel}>{resolveStepLabel(step.step_name)}</span>
        <span style={s.stepCount}>{step.user_count.toLocaleString()}</span>
      </div>
      <div style={s.barTrack}>
        <div
          style={{
            ...s.bar,
            width: `${widthPercent}%`,
            background: barColor,
          }}
        />
      </div>
    </div>
  );
}

type ConversionLabelProps = {
  readonly fromCount: number;
  readonly toCount: number;
};

function ConversionLabel({
  fromCount,
  toCount,
}: ConversionLabelProps): React.JSX.Element {
  const percent = fromCount > 0 ? Math.round((toCount / fromCount) * 100) : 0;
  return (
    <div style={s.conversionLabel}>
      {t("funnel.conversion_rate", { percent })}
    </div>
  );
}

export function FunnelMini({
  steps,
  onStepClick,
}: FunnelMiniProps): React.JSX.Element {
  if (steps.length === 0) {
    return (
      <div style={s.container}>
        <span style={s.empty}>{t("funnel.no_funnel_data")}</span>
        <span style={s.emptyHint}>{t("funnel.empty_hint")}</span>
      </div>
    );
  }

  const firstCount = steps[0]?.user_count ?? 0;

  return (
    <div style={s.container}>
      {steps.map((step, i) => {
        const widthPercent =
          firstCount > 0 ? Math.round((step.user_count / firstCount) * 100) : 0;
        const prevCount =
          i > 0 ? (steps[i - 1]?.user_count ?? 0) : step.user_count;
        const barColor = resolveBarColor(
          step.user_count,
          firstCount,
          prevCount,
        );

        return (
          <div key={step.step_name} style={s.stepWrapper}>
            <StepRow
              step={step}
              widthPercent={widthPercent}
              barColor={barColor}
              onStepClick={onStepClick}
            />
            {i < steps.length - 1 && (
              <ConversionLabel
                fromCount={step.user_count}
                toCount={steps[i + 1]?.user_count ?? 0}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: 400,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  stepWrapper: {
    display: "flex",
    flexDirection: "column",
  },
  stepRow: {
    display: "flex",
    flexDirection: "column",
  },
  stepHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  stepLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: "var(--color-text)",
  },
  stepCount: {
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "var(--font-mono)",
    color: "var(--color-text)",
  },
  barTrack: {
    marginTop: 4,
    width: "100%",
    background: "var(--color-border)",
    borderRadius: 3,
    height: 6,
    overflow: "hidden",
  },
  bar: {
    height: 6,
    borderRadius: 3,
    transition: "width 0.3s ease",
  },
  conversionLabel: {
    fontSize: 11,
    color: "var(--color-muted)",
    textAlign: "center",
    padding: "2px 0",
  },
  empty: {
    fontSize: 13,
    color: "var(--color-text-secondary)",
  },
  emptyHint: {
    fontSize: 12,
    color: "var(--color-muted)",
    marginTop: 4,
    lineHeight: 1.5,
  },
};
